import { ProcessingStep } from './kyc-upload.types';

/**
 * SomosKycV1-only completion-step model (v2 — unified 5-step stepper).
 *
 * The other KYC providers (verifflite / veriff / thirdfort / textract) drive the PROCESSING
 * screen off a fixed 4-step list and a fake 2s timer. SomosKycV1 shows the stepper the moment
 * the guided capture completes and drives it off what actually happens:
 *
 *  1. READING        — pre-check upload + OCR extract (no-QR) / QR decode (done at capture)
 *  2. CONFIRMING     — pre-submit user decisions (pre-review / CPF-ack / degraded-check)
 *  3. SUBMITTING     — storage upload (QR path) + the /kyc_check submit
 *  4. PRE_PROCESSING — registry validation of the personal data (cp_kyc_check stage)
 *  5. PROCESSING     — document analysis (cp_kyc_check stage)
 *
 * Steps 1-3 are frontend-driven (explicit calls on the capture/submit path); steps 4-5 are
 * stage-driven via {@link mapSomosV1StageToSteps}, kept pure so it can be unit-tested without
 * the component. Labels are generic — no institution/vendor names (SERPRO, Receita, Veriff).
 */

/** The steps SomosKycV1 renders — no 'UPLOADING' (the storage upload folds into SUBMITTING). */
export type SomosV1ProcessingStep = Exclude<ProcessingStep, 'UPLOADING'>;

/** Ordered five-step model shown on the SomosKycV1 PROCESSING screen. */
export const SOMOS_V1_STEP_ORDER: SomosV1ProcessingStep[] = [
  'READING',
  'CONFIRMING',
  'SUBMITTING',
  'PRE_PROCESSING',
  'PROCESSING',
];

/** Per-step label (ngx-translate key). */
export const SOMOS_V1_STEP_LABEL_KEYS: Record<SomosV1ProcessingStep, string> = {
  READING: 'KycStepReadingDocument',
  CONFIRMING: 'KycStepConfirmingDetails',
  SUBMITTING: 'KycStepSendingVerification',
  PRE_PROCESSING: 'KycStepValidatingPersonalData',
  PROCESSING: 'KycStepAnalyzingDocument',
};

/** Per-step muted sub-line (ngx-translate key) — the template renders it on the active step only. */
export const SOMOS_V1_STEP_HINT_KEYS: Record<SomosV1ProcessingStep, string> = {
  READING: 'KycStepReadingDocumentHint',
  CONFIRMING: 'KycStepConfirmingDetailsHint',
  SUBMITTING: 'KycStepSendingVerificationHint',
  PRE_PROCESSING: 'KycStepValidatingPersonalDataHint',
  PROCESSING: 'KycStepAnalyzingDocumentHint',
};

/** Sub-line shown when a step is paused waiting on the user (modals / name gate / CPF correction). */
export const SOMOS_V1_ACTION_NEEDED_HINT_KEY = 'KycStepActionNeededHint';

/** Resolved state of one completion step for a given stage. */
export interface SomosV1StepState {
  step: SomosV1ProcessingStep;
  done: boolean;
  active: boolean;
  actionNeeded: boolean;
  /** Sub-line translation key. Always set; the template renders it only on the active step. */
  descriptionKey: string;
}

/** Full five-step resolution plus which step the spinner sits on. */
export interface SomosV1StepSync {
  currentStep: SomosV1ProcessingStep;
  steps: SomosV1StepState[];
}

/** Steps before `active` are done; `active` is active (optionally action-needed); the rest pending. */
function activeAt(
  active: SomosV1ProcessingStep,
  opts?: { actionNeeded?: boolean },
): SomosV1StepSync {
  const activeIdx = SOMOS_V1_STEP_ORDER.indexOf(active);
  const actionNeeded = !!opts?.actionNeeded;
  const steps = SOMOS_V1_STEP_ORDER.map<SomosV1StepState>((step, idx) => {
    const isActive = idx === activeIdx;
    return {
      step,
      done: idx < activeIdx,
      active: isActive,
      actionNeeded: isActive && actionNeeded,
      descriptionKey:
        isActive && actionNeeded
          ? SOMOS_V1_ACTION_NEEDED_HINT_KEY
          : SOMOS_V1_STEP_HINT_KEYS[step],
    };
  });
  return { currentStep: active, steps };
}

/** All five steps done — used for the terminal stages; failure handlers then flag the erroring step. */
function allDone(): SomosV1StepSync {
  const steps = SOMOS_V1_STEP_ORDER.map<SomosV1StepState>((step) => ({
    step,
    done: true,
    active: false,
    actionNeeded: false,
    descriptionKey: SOMOS_V1_STEP_HINT_KEYS[step],
  }));
  return { currentStep: 'PROCESSING', steps };
}

/**
 * Pure map from the observed `cp_kyc_check.stage` to the five completion-step states. The
 * pre-submit steps (READING/CONFIRMING/SUBMITTING) resolve as done for every observable stage —
 * a check only exists once they finished.
 *
 * Returns `null` for stages that shouldn't disturb the UI (unknown / unhandled), so the caller
 * leaves the current step states untouched.
 */
export function mapSomosV1StageToSteps(
  stage: string | undefined | null,
): SomosV1StepSync | null {
  switch ((stage || '').toUpperCase()) {
    case 'PENDING':
    case 'PRE_PROCESSING':
      // Registry validation in flight → "Validating your personal data".
      return activeAt('PRE_PROCESSING');
    case 'AWAITING_NAME_CONFIRMATION':
      // Paused at the name gate — the user must confirm before the flow continues.
      return activeAt('PRE_PROCESSING', { actionNeeded: true });
    case 'PROCESSING':
      // Personal data cleared → document analysis in flight.
      return activeAt('PROCESSING');
    case 'DONE':
    case 'RESUBMISSION':
    case 'CANCELLED':
    case 'CANCELED':
    case 'ERROR':
      return allDone();
    default:
      return null;
  }
}
